import {mergeDeepRight} from 'ramda';
import isEqual from 'lodash/isEqual';
import React, {useEffect, useState} from 'react';
import {
  View,
  Pressable,
  ViewStyle,
  TextStyle,
  StyleProp,
} from 'react-native';
import colors from 'theme/Colors';
import {corner_radius, spacing} from 'theme/Units';
import {SearchDropDown} from './SearchDropDown';
import {Icon, Text} from 'ui-kits';
import {IconType} from 'constants/icons';

type sizeTypes = 'lg' | 'md' | 'sm';

interface MultiSelectDropdownProps {
  options: any[];
  label?: string;
  size?: sizeTypes;
  placeholder?: string;
  helperLabel?: string;
  optionalLabel?: string;
  searchPlaceholder?: string;
  required?: boolean;
  error?: string;
  selectedEntries?: any[];
  onSave: (selectedOptions: any[]) => void;
  keyExtractor: (item: any) => string;
  searchKeyExtractor: (item: any) => string;
  renderItem: (item: any) => JSX.Element;
  displayItem?: (item: any[]) => JSX.Element;
  singleSelect?: boolean;
  rightIcon?: IconType;
  dropDownStyle?: ViewStyle;
  searchHeader?: string;
  fetchNextHandler?: () => void;
  style: StyleProp<ViewStyle>;
}

export const MultiSelect: React.FC<MultiSelectDropdownProps> = ({
  options,
  label,
  size = 'md',
  placeholder = 'Add member',
  searchPlaceholder = 'Search by name',
  keyExtractor,
  renderItem,
  helperLabel,
  optionalLabel,
  required,
  searchKeyExtractor,
  selectedEntries,
  singleSelect = false,
  error,
  rightIcon = 'plus',
  fetchNextHandler,
  dropDownStyle,
  displayItem = items => <Text>{`${items.length} selected`}</Text>,
  onSave,
  searchHeader,
  style: overrideStyles,
}) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const styles = mergeDeepRight(baseStyles, sizeStyles[size]) as styleProps;

  const [selectedOptions, setSelectedOptions] = useState<any[]>(
    selectedEntries || [],
  );

  function handleSave(savedOptions: any[]) {
    setSelectedOptions(savedOptions);
    onSave?.(savedOptions);
    setIsDropdownVisible(false);
  }

  useEffect(() => {
    if (selectedOptions.length) {
      setSelectedOptions(prev => {
        return !isEqual(prev, selectedOptions) ? selectedOptions : prev;
      });
    }
  }, [selectedOptions]);

  useEffect(() => {
    if (selectedEntries) {
      setSelectedOptions(selectedEntries);
    }
  }, [selectedEntries]);

  return (
    <>
      <View style={[styles.container, overrideStyles]}>
        {/* label and optional text */}
        <View style={styles.labelWrapper}>
          <Text style={styles.label}>
            {label}{' '}
            {required ? <Text style={styles.redAsterisk}>*</Text> : null}
          </Text>
          <Text style={styles.optionnalLabel}>{optionalLabel}</Text>
        </View>

        {/* input wrapper */}
        <Pressable
          style={styles.inputWrapper}
          onPress={() => setIsDropdownVisible(!isDropdownVisible)}>
          {/* placeholder/text for  */}
          <Text style={styles.placeholder}>
            {selectedOptions.length > 0
              ? displayItem(selectedOptions)
              : placeholder}
          </Text>

          {/* plus icon */}
          <Icon type={rightIcon} size={14} />
        </Pressable>
        {helperLabel ? (
          <Text style={styles.helperLabel}>{helperLabel}</Text>
        ) : null}
        {error ? <Text style={styles.errorLabel}>{error}</Text> : null}
      </View>

      {/* dropdown renderer */}
      {isDropdownVisible ? (
        <SearchDropDown
          selectedEntries={selectedOptions}
          searchKeyExtractor={searchKeyExtractor}
          searchPlaceholder={searchPlaceholder}
          onClose={() => setIsDropdownVisible(false)}
          onSave={handleSave}
          options={options}
          optionKeyExtractor={keyExtractor}
          renderOption={renderItem}
          singleSelect={singleSelect}
          style={dropDownStyle}
          header={searchHeader}
          fetchNextData={fetchNextHandler}
        />
      ) : null}
    </>
  );
};

type styleProps = {
  label: TextStyle;
  placeholder: TextStyle;
  container: ViewStyle;
  inputWrapper: ViewStyle;
  helperLabel: TextStyle;
  optionnalLabel: TextStyle;
  errorLabel: TextStyle;
  redAsterisk: TextStyle;
  labelWrapper: ViewStyle;
};

const baseStyles: styleProps = {
  labelWrapper: {flexDirection: 'row', justifyContent: 'space-between'},
  redAsterisk: {color: colors.palette.errorRedDark},
  container: {
    minWidth: 0,
    width: '100%',
    gap: spacing['2'],
    backgroundColor: colors.palette.white,
  },
  inputWrapper: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: colors.palette.stroke,
    borderRadius: corner_radius['rounded-md'],
    padding: spacing[2],
    justifyContent: 'space-between',
  },
  label: {
    color: colors.palette.black,
    fontWeight: '600',
    fontSize: 16,
  },
  placeholder: {
    color: colors.palette.nuetral4,
    flex: 1,
    marginLeft: 8,
    fontWeight: '500',
  },
  helperLabel: {
    color: colors.palette.secondaryBlack5,
    fontSize: 12,
    fontWeight: '400',
  },
  optionnalLabel: {
    color: colors.palette.secondaryBlack5,
    fontSize: 12,
    fontWeight: '500',
  },
  errorLabel: {
    color: colors.palette.errorRedDark,
    fontSize: 12,
    fontWeight: '400',
  },
};

const sizeStyles: Record<sizeTypes, Partial<styleProps>> = {
  lg: {
    inputWrapper: {
      height: 48,
      fontSize: 18,
    },
  },
  md: {
    inputWrapper: {
      height: 40,
      fontSize: 14,
    },
  },
  sm: {
    inputWrapper: {
      height: 32,
      fontSize: 14,
    },
    label: {
      fontSize: 14,
    },
    placeholder: {
      fontSize: 14,
    },
  },
};
